<template>
    <div v-if="rendering">
        <div
            class="w-full mb-8 rounded-md shadow-[0_2px_12px_-3px_rgba(0,0,0,0.25)] h-[60px] bg-white p-1 box-border relative">
            <div ref="carousel" class="carousel" :class="!flkty && 'flex overflow-hidden flex-nowrap'">
                <div
                    class="carousel-cell"
                    v-for="(headline, index) in headlines" :key="headline.uid"
                >
                    <div
                        class="flex w-fit h-[52px] items-center justify-between px-4 cursor-pointer whitespace-nowrap text-body-6 relative"
                        :class="[
                            index === headlineActiveIndex ? `rounded-[6px] text-black` : 'text-default-3 hover:text-primary-1',
                            (index !== headlineActiveIndex && headlineActiveIndex - index !== 1) && 'border-r last:border-r-0 border-interactive-9',
                        ]"
                        @click="selectTab(index)"
                    >
                        <div
                            v-if="showRequiredIndicator && isRequired(headline)"
                            @mouseenter="setTooltipPosition($event)"
                            @mouseleave="tooltipPosition = null"
                            class="w-[14px] h-[14px] absolute top-1 right-1 bg-[#FF7474] rounded-full text-white flex items-center justify-center cursor-pointer">
                            !
                        </div>

                        <div v-if="index === headlineActiveIndex"
                             class="absolute left-0 top-0 w-full h-full bg-primary-1 opacity-20 rounded-[6px]"></div>

                        <span class="z-10" :class="index === headlineActiveIndex && 'text-primary-1'">{{
                                headline.label
                            }}</span>
                    </div>
                </div>
            </div>
        </div>
        <TransitionRoot as="template" :show="!!tooltipPosition">
            <TransitionChild
                enter="transition-opacity ease-linear duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0">
                <div
                    v-if="tooltipPosition"
                    :style="`left: ${tooltipPosition.left}px; top: ${tooltipPosition.top}px`"
                    class="z-10 w-fit absolute bottom-0 left-0 rounded-[3px] text-[#983C3C] bg-background-5 text-body-7 px-2 py-1 h-fit -translate-x-[50%] -translate-y-[190%]">
                    This category contains required fields
                    <div
                        class="arrow-down absolute -bottom-2 top-auto left-1/2 -translate-x-1/2 !border-t-[#FFBBBB]"></div>
                </div>
            </TransitionChild>
        </TransitionRoot>
    </div>
</template>

<script setup>
import {TransitionChild, TransitionRoot} from '@headlessui/vue';
import {nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import Flickity from 'flickity';
import 'flickity/dist/flickity.min.css';
import 'hint.css/hint.min.css';

const carousel = ref(null);
let flkty = ref(null);
const rendering = ref(true);

const tooltipPosition = ref(null);

const props = defineProps({
    headlines: {
        type: Array,
        required: true
    },
    headlineActiveIndex: {
        type: Number,
        required: true
    },
    showRequiredIndicator: {
        type: Boolean,
        default: true
    }
})

defineExpose({selectTab, killFlickity, initFlickity})

const emit = defineEmits(['select-tab', 'set-arrow-position'])

function selectTab(index) {
    if(!flkty.value) {
        return;
    }
    
    flkty.value.select(index, false, true);
}

function isRequired(headline) {
    return headline.fields.some(field => field.is_required);
}

function setTooltipPosition(event) {
    if (tooltipPosition.value) {
        return;
    }

    const targetElement = event.currentTarget;
    const rect = targetElement.getBoundingClientRect();

    tooltipPosition.value = {
        left: rect.left + window.scrollX + rect.width / 2,
        top: rect.top + window.scrollY + rect.height / 2
    }
}

async function initFlickity() {
    flkty.value = new Flickity(carousel.value, {
        cellAlign: 'left',
        initialIndex: 0,
        groupCells: false,
        contain: true,
        pageDots: false,
    });

    flkty.value.on('select', function (index) {
        emit('select-tab', index);

        const elem = flkty.value.selectedElement;

        const rect = elem.getBoundingClientRect();

        const coords = {
            left: ((rect.left + window.scrollX + rect.width / 2) - 15),
            top: rect.top + window.scrollY + rect.height,
            x: elem.x,
            y: elem.y,
            width: rect.width,
        }

        emit('set-arrow-position', coords);
    });

    return flkty.value;
}

function killFlickity() {
    flkty.value.destroy();
    flkty.value = null;
}

onMounted(async () => {
    await initFlickity();

    nextTick(() => {
        selectTab(0);
    })
})

onUnmounted(() => {
    killFlickity();
})

watch(() => props.headlines, (newVal, oldVal) => {
    if (flkty.value) {
        rendering.value = false;
        flkty.value.destroy();
    }

    nextTick(() => {
        rendering.value = true;
        nextTick(() => {
            initFlickity();
        })
    })
});
</script>

<style scoped lang="scss">
:deep(.carousel) {
    outline: none;

    .flickity-viewport {
        outline: none;
    }

    .flickity-button {
        display: none;
    }

    &:hover {
        .flickity-button {
            display: flex;
        }
    }

    .flickity-button {
        background: none;

        &:disabled {
            display: none;
        }

        &.flickity-prev-next-button {
            border-radius: 0;
            height: 60px;

            &.previous {
                .flickity-button-icon {
                    right: 75%;
                }
            }

            &.next {
                .flickity-button-icon {
                    left: 75%;
                }
            }

            .flickity-button-icon {
                width: 30%;
                height: 30%;
                top: 50%;

                transform: translate(-50%, -50%);
            }
        }

        &.previous {
            left: -4px;
            background-image: linear-gradient(90deg, #EEEEEE 0%, rgba(216, 216, 216, 0.00) 100%);
            border-radius: 5px 0 0 5px;
        }

        &.next {
            right: -4px;
            background-image: linear-gradient(-90deg, #EEEEEE 0%, rgba(216, 216, 216, 0.00) 100%);
            border-radius: 0 5px 5px 0;
        }

        &:focus {
            box-shadow: none;
        }
    }
}

</style>